import React, { useState, useEffect, useCallback } from 'react';
import { FaUserPlus, FaSignInAlt, FaChevronDown } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const heroImages = [
  '/images/gallery-1.jpg',
  '/images/gallery-2.jpg',
  '/images/gallery-3.jpg',
  '/images/gallery-4.jpg',
  '/images/gallery-5.jpg',
  '/images/gallery-6.jpg',
  '/images/gallery-7.jpg',
  '/images/gallery-8.jpg',
];

const Hero = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextImage = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % heroImages.length);
  }, []);

  useEffect(() => {
    const interval = setInterval(nextImage, 10000);
    return () => clearInterval(interval);
  }, [nextImage]);

  const handleScrollDown = () => {
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image Slideshow */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentIndex}
          initial={{ opacity: 0, scale: 1.1 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.5 }}
          className="absolute inset-0"
        >
          <img
            src={heroImages[currentIndex]}
            alt="Tabla performance"
            className="w-full h-full object-contain bg-maroon-dark"
          />
        </motion.div>
      </AnimatePresence>

      {/* Dark Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-maroon-dark/80 via-maroon/70 to-maroon-dark/90 z-[1]" />

      {/* Slide Indicators */}
      <div className="absolute bottom-24 left-1/2 -translate-x-1/2 z-20 flex space-x-2">
        {heroImages.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrentIndex(i)}
            className={`w-2 h-2 rounded-full transition-all duration-500 ${i === currentIndex
                ? 'bg-gold w-6'
                : 'bg-gold-light/30 hover:bg-gold-light/50'
              }`}
            aria-label={`Go to slide ${i + 1}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Decorative gold line */}
          <motion.div 
            initial={{ width: 0 }}
            animate={{ width: 80 }}
            transition={{ duration: 1.2, delay: 0.2 }}
            className="h-0.5 bg-gold mx-auto mb-8" 
          />

          {/* Main Title */}
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight">
            Master the{' '}
            <span className="text-gradient-gold">Rhythm</span>
            <br />
            of <span className="text-gradient-gold">Tabla</span>
          </h1>

          {/* Subtitle */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="font-sans text-lg sm:text-xl md:text-2xl text-gold-light/80 mb-4 tracking-wide"
          >
            Tabla Maestro &nbsp;•&nbsp; Performer &nbsp;•&nbsp; Guru
          </motion.p>

          {/* Teacher name */}
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.7 }}
            className="font-serif text-xl sm:text-2xl text-gold italic mb-10"
          >
            — Atharva Kagalkar
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9 }}
            className="flex flex-col sm:flex-row gap-5 justify-center items-center mb-12"
          >
            <Link to="/register" className="btn-gold text-base tracking-widest hover:scale-105 transition-transform duration-300">
              <FaUserPlus className="inline mr-3 text-sm" />
              START LEARNING
            </Link>
            <Link
              to="/login"
              className="flex items-center px-8 py-3.5 text-gold-light border border-gold/40 rounded-full hover:bg-gold/10 hover:border-gold transition-all duration-300 font-medium tracking-wide"
            >
              <FaSignInAlt className="mr-3 text-sm" />
              Login to Continue
            </Link>
          </motion.div>

          {/* Stats */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="grid grid-cols-3 gap-8 max-w-lg mx-auto"
          >
            {[
              { num: '10+', label: 'Years Experience' },
              { num: '500+', label: 'Students Worldwide' },
              { num: '15+', label: 'Countries' }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-serif font-bold text-gold mb-1">{stat.num}</div>
                <div className="text-gold-light/60 text-xs sm:text-sm font-sans tracking-wider uppercase">{stat.label}</div>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Down Indicator */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5, duration: 1 }}
        onClick={handleScrollDown}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gold-light/60 hover:text-gold transition-colors duration-300 cursor-pointer z-20"
        aria-label="Scroll down"
      >
        <div className="flex flex-col items-center space-y-2">
          <span className="text-xs font-sans tracking-[0.2em] uppercase">Scroll</span>
          <motion.div
            animate={{ y: [0, 5, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
          >
            <FaChevronDown className="text-sm" />
          </motion.div>
        </div>
      </motion.button>
    </section>
  );
};

export default Hero;